import { css } from 'lit';
import { CCTVDeviceTypes, MILLISECONDS_PER_MINUTE, MILLISECONDS_PER_SECOND } from '@constants';
import { CameraDto } from '@typings/api';

export const USER_CAN_TOGGLE_CAMERA_FEED = true;

export const cctvColumns = <const>[
	'type',
	'cameraNumber',
	'description',
	'name',
	'ipAddress',
	'status',
];

export const activeCctvColumns: (typeof cctvColumns)[number][] = [
	'type',
	'cameraNumber',
	'description',
	'name',
	'ipAddress',
	...(USER_CAN_TOGGLE_CAMERA_FEED ? ['status'] : []),
] as (typeof cctvColumns)[number][];

export enum SideContentSections {
	VIEWS = 'VIEWS',
	LIST = 'LIST',
	MAP = 'MAP',
	VIDEO_WALL = 'VIDEO WALL',
}

export const ConfigCCTV = {
	Grids: [1, 4, 9, 16], //  controls which grid layout options are listed for monitors

	//  camera thumbnails will be constructed thus: [CamThumbRoot]+[Camera.name]+[CamThumbExt]
	CamThumbRoot: 'http://content.trafficwise.org/cctv/',
	CamThumbExt: '.jpg',

	Map: {
		//  Leaflet map will center on this point, at the provided zoom level
		//  https://leafletjs.com/reference-1.4.0.html#map-setview
		DefaultLat: 39.76632525654491,
		DefaultLon: -86.15615844726564,
		DefaultZoom: 12,
		MinZoom: 7,
		MaxZoom: 18,
		SpiderifyDistancePx: 10,
		SpiderifyLegThicknessPx: 3,
		SpiderifyLegColorNormal: 'rgba(var(--brand-grey-dark),0.6)',
		SpiderifyLegColorFocus: 'rgba(var(--brand-grey-dark),1)',
	},
	Table: {
		pagination: [5, 15, 50, 100],
		defaultColumns: [
			'cameraNumber',
			'description',
			'name',
			...(USER_CAN_TOGGLE_CAMERA_FEED ? ['status'] : []),
		] as (typeof cctvColumns)[number][],
	},
	WEB_APPLICATION_SOURCE: 'INDOT_carsx',
	FEED_SERVICE_SOURCE: 'INDOT_Feed',
	ADMIN_ROLE_ID: 1,
	cameraControlEnabled: false,
	monitorControlEnabled: false,
	sideContentSectionIsVisible: {
		[SideContentSections.VIEWS]: false,
		[SideContentSections.LIST]: false,
		[SideContentSections.MAP]: false,
		[SideContentSections.VIDEO_WALL]: true,
	},
	streamTimeoutMillis: 60 * MILLISECONDS_PER_MINUTE,
	showCameraFilters: true,
	cameraEditorIsInATMSMode: false,
};

const CameraIconImageFiles = {
	CCTV: 'img/icon-camera-fill-solid-2.svg',
	HH: 'img/icon-truck-blue-fill-solid.svg',
};

export const CameraIconByType: Record<CCTVDeviceTypes, string> = {
	[CCTVDeviceTypes.NONE]: CameraIconImageFiles.CCTV,
	[CCTVDeviceTypes.cctv]: CameraIconImageFiles.CCTV,
	[CCTVDeviceTypes.cctv_rwis]: CameraIconImageFiles.CCTV,
	[CCTVDeviceTypes.cctv_city]: CameraIconImageFiles.CCTV,
	[CCTVDeviceTypes.cctv_irv]: CameraIconImageFiles.HH,
	[CCTVDeviceTypes.cctv_irv_drn]: CameraIconImageFiles.HH,
	[CCTVDeviceTypes.cctv_irv_usb]: CameraIconImageFiles.HH,
	[CCTVDeviceTypes.cctv_port]: CameraIconImageFiles.CCTV,
	[CCTVDeviceTypes.cctv_sig]: CameraIconImageFiles.CCTV,
	[CCTVDeviceTypes.cctv_tpa]: CameraIconImageFiles.CCTV,
	[CCTVDeviceTypes.cctv_tps]: CameraIconImageFiles.CCTV,
	[CCTVDeviceTypes.cctv_dms]: CameraIconImageFiles.CCTV,
	[CCTVDeviceTypes.cctv_wz]: CameraIconImageFiles.CCTV,
	[CCTVDeviceTypes.cctv_test]: CameraIconImageFiles.CCTV,
};

export const ConfigCCTVApi = {
	//  CAMERAS
	getCamerasEndpoint(): string {
		return 'devices';
	},
	getRetrieveDeviceEndpoint(deviceId: number): string {
		return `devices/${deviceId}`;
	},
	getMatchCameraNumberEndpoint(cameraNumber: number): string {
		return `devices/validate/number/${cameraNumber}`;
	},
	getMatchCameraIdEndpoint(friendlyName: string): string {
		return `devices/validate/friendlyName/${friendlyName}`;
	},
	getSaveDeviceEndpoint(): string {
		return 'devices'; // POST
	},
	getDeleteDeviceEndpoint(deviceId: number): string {
		return `devices/${deviceId}`; // DELETE
	},
	getEndpointTimeoutMs(): number {
		return 60 * MILLISECONDS_PER_SECOND;
	},
	getCameraRolesEndpoint(): string {
		return `devices/roles`;
	}, //  GET
	//  MONSTREAM CONTROLLERS
	getMonitorsEndpoint(): string {
		return `controllers`;
	}, //  GET
	getMonitor: (id: string | number) => `controllers/${id}`,
	getSetMonitorEndpoint(name: string): string {
		return `controllers/${name}/cameras`;
	}, //  POST
	getSetMonitorLayoutEndpoint(name: string): string {
		return `controllers/${name}/layout`;
	}, //  POST
	getCameraThumbUrl(id: string | number): string {
		return `cameras/${id}/stillshot`;
	},
	setCameraOnline: (id: string | number) => `cameras/${id}/online`,
	setCameraOffline: (id: string | number) => `cameras/${id}/offline`,
	setCameraZoomRatio: (id: string | number) => `cameras/${id}/setZoomRatio`,
	getCameraZoomRatio: (id: string | number) => `cameras/${id}/getZoomRatio`,
	panTiltCameraToFieldOfView: (id: string | number) => `cameras/${id}/panTiltToFov`,
	ptzStatus: () => 'cameras/ptzStatus',
	setAllCamerasOnline: () => 'cameras/onlineAll',
	setAllCamerasOffline: () => 'cameras/offlineAll',
	allCurrentEditors: 'devices/currentEditors',
	currentEditorsForDevice: (id: number) => `devices/${id}/currentEditors`,
	cameraPreset: (id: number) => `cameras/${id}/preset`,
	moveCameraToPresetNumber: (id: number) => `cameras/${id}/activatePresetNumber`,
	moveCameraToPresetName: (id: number) => `cameras/${id}/activePresetName`,
	viewsets: 'controllers/viewsetNames',
	viewsetCameras: (name: string) => `controllers/viewsets/${name}`,
	endpointTimeoutMs: 60 * MILLISECONDS_PER_SECOND,
	camerasPollingRate: 60 * MILLISECONDS_PER_SECOND,
	monitorsPollingRate: 6 * MILLISECONDS_PER_SECOND,
	allCurrentEditorsPollingRateMillis: 10 * MILLISECONDS_PER_SECOND,
	currentEditorsPollingRateMillis: 5 * MILLISECONDS_PER_SECOND,
	ptzStatusPollingRateMillis: 5 * MILLISECONDS_PER_SECOND,
};

export const getCameraTitle = (cameraDTO: CameraDto | null) =>
	!cameraDTO ? `--` : `#${cameraDTO.cameraNumber} ${cameraDTO.description} (${cameraDTO.name})`;

export default ConfigCCTV;
